// Offline queue for photos + daily logs + time events (PWA resilience).
// Photos keep their FULL proof metadata (blob, client_id, GPS fix, shutter
// time, timezone, source) so a queued upload is identical to a live one.
// States: pending → uploading → uploaded | failed | orphaned | auth |
// needs_project | unreadable. Nothing retryable is ever stranded.
//
// Fast Photos (B/E) + STOP-SHIP stress fixes:
// - uploads start the moment a photo is queued, 3 at a time
// - a Web Lock + in-flight set + 60s lease mean an item is NEVER sent twice
// - error taxonomy: 401 → "auth" (sign-in prompt), 408/429/5xx → backoff
//   forever, 413 → recompress smaller and retry, 400/422 → failed with the
//   real error, 404/410 → "orphaned" (job gone, kept for Save to phone)
// - migrateQueue() upgrades legacy records (blob under file/blob/data,
//   project_id key) on every app open — old-format photos can never strand
//
// STOP-SHIP Wave 2 (lockup fix — the "Done spins forever" bug):
// - ONE shared IndexedDB connection; every transaction is hard-bounded
//   (5s → abort, connection reset, report to /api/photos/client-error)
// - every network call is hard-bounded (netTimeout) — a hung request can
//   no longer hold the drain, the "bf-flush" lock, or the flushing flag
// - the Web Lock has a 60s max hold (AbortSignal) and flushQueue a 75s
//   watchdog, so the queue ALWAYS recovers
// - Done never waits >2s on the store: slow saves go to the in-memory
//   rescue loop (background retries + "Saving N photos…" pill)
// - migration works on a snapshot; one bad item can never stall the rest
import { prepareImageForUpload } from "@/lib/imagePrep";
import { api as telemetryApi, API, setToken } from "@/lib/api";

const DB_NAME = "buildflow-offline";
const STORE = "queue";
const DB_VERSION = 2; // v2: by_client_id index (O(1) dedupe)
const UPLOADED_KEEP_MS = 60 * 60 * 1000; // "Uploaded" confirmations visible for 1h
const CONCURRENCY = 3;
const BACKOFF_MS = [5000, 15000, 30000, 60000, 120000];
const DEAD_FAILS = 10;
const UPLOAD_LEASE_MS = 60000;
const PREPARING_STALE_MS = 60000; // a stuck resize must never keep a photo out of the drain
const STATUS_REPORT_MS = 15 * 60 * 1000;

// Hard bounds (exported so tests can shrink them). NOTHING in this file
// is allowed to wait forever.
export const QUEUE_LIMITS = {
  idbMs: 5000,            // every IndexedDB op aborts + reports after this
  lockHoldMs: 60000,      // the "bf-flush" Web Lock is released after this
  flushWatchdogMs: 75000, // the flushing flag always resets after this
  netMs: { photo: 30000, original: 120000, log: 15000, time_event: 15000, refresh: 15000, status: 10000 },
};

let flushing = false;
const inFlight = new Set(); // qids currently uploading — never double-send
const listeners = new Set();

function notify() {
  listeners.forEach((cb) => { try { cb(); } catch (e) { /* a listener must not break the queue */ } });
  try { window.dispatchEvent(new Event("bf:queue-changed")); } catch (e) { /* non-browser env */ }
}

export function subscribeQueue(cb) { listeners.add(cb); return () => listeners.delete(cb); }

function isOnWifiNow() {
  try {
    const c = navigator.connection;
    if (!c) return true; // iOS: no Network Information API — treat online as OK
    if (c.type) return c.type === "wifi";
    if (c.saveData) return false;
    return c.effectiveType === "4g";
  } catch { return true; }
}

const backoffFor = (attempts) => BACKOFF_MS[Math.min(Math.max(attempts, 1), BACKOFF_MS.length) - 1];

// Fast Photos (Wave 2): fire-and-forget queue error telemetry — stuck phones
// show up on Admin > Upload health without waiting for a user report.
export function reportQueueError(api, kind, message, item) {
  try {
    const a = api || telemetryApi;
    Promise.resolve(a.post("/photos/client-error", {
      kind, message: String(message || "").slice(0, 280), client_id: (item && item.client_id) || "",
    })).catch(() => {});
  } catch { /* telemetry must never break the queue */ }
}

// Legacy records stored the photo under other keys ("file", "blob", "data")
// and the job under "project_id". Normalize everywhere we read.
export const blobOf = (rec) =>
  rec?.fileBlob || rec?.file || rec?.blob || (rec?.data instanceof Blob ? rec.data : null) || null;
export const projectOf = (rec) => rec?.projectId ?? rec?.project_id ?? "";

// ---- IndexedDB core: ONE shared connection, every op hard-bounded ----
let dbPromise = null;
function openDB() {
  if (!dbPromise) {
    dbPromise = new Promise((resolve, reject) => {
      const req = indexedDB.open(DB_NAME, DB_VERSION);
      req.onupgradeneeded = (e) => {
        const d = req.result;
        // v2: by_client_id index — dedupe used to getAll() the whole store
        // (deserializing every queued blob), which is why Done ate the full
        // 1s cap on a busy queue.
        if (!d.objectStoreNames.contains(STORE)) {
          d.createObjectStore(STORE, { keyPath: "qid", autoIncrement: true })
            .createIndex("by_client_id", "client_id", { unique: false });
        } else if (e.oldVersion < 2) {
          const store = req.transaction.objectStore(STORE);
          if (!store.indexNames.contains("by_client_id")) {
            store.createIndex("by_client_id", "client_id", { unique: false });
          }
        }
      };
      req.onsuccess = () => {
        try { req.result.onversionchange = () => { try { req.result.close(); } catch { /* noop */ } dbPromise = null; }; } catch { /* noop */ }
        resolve(req.result);
      };
      req.onerror = () => { dbPromise = null; reject(req.error); };
      req.onblocked = () => { /* another tab holds v1 open — its onversionchange closes it; idbTx bounds the wait */ };
    });
  }
  return dbPromise;
}
async function hardResetDB() {
  try { const d = await dbPromise; if (d) d.close(); } catch { /* already broken */ }
  dbPromise = null;
}

const reqP = (req) => new Promise((res, rej) => { req.onsuccess = () => res(req.result); req.onerror = () => rej(req.error); });

// One bounded IndexedDB transaction. fn gets the object store; use reqP()
// for requests. A stuck store aborts, resets the shared connection and
// reports — it can NEVER freeze the queue (or the camera's Done) again.
async function idbTx(label, mode, fn) {
  let tx = null;
  let timer = null;
  const work = (async () => {
    const db = await openDB(); // inside the race — a BLOCKED version upgrade
    return new Promise((resolve, reject) => { // must not hang the queue either
      tx = db.transaction(STORE, mode);
      const store = tx.objectStore(STORE);
      let out;
      Promise.resolve().then(() => fn(store)).then((v) => { out = v; }, reject);
      tx.oncomplete = () => resolve(out);
      tx.onerror = () => reject(tx.error || new Error("idb_error"));
      tx.onabort = () => reject(new Error("idb_aborted"));
    });
  })();
  try {
    return await Promise.race([
      work,
      new Promise((_, rej) => {
        timer = setTimeout(() => {
          try { if (tx) tx.abort(); } catch { /* noop */ }
          rej(new Error(`idb_timeout:${label}`));
        }, QUEUE_LIMITS.idbMs);
      }),
    ]);
  } catch (e) {
    if (String((e && e.message) || "").startsWith("idb_timeout")) {
      hardResetDB();
      reportQueueError(null, "idb_timeout", label, null);
    }
    throw e;
  } finally {
    clearTimeout(timer);
  }
}

// No network call in the drain may hang forever — a hung request used to
// hold the Web Lock + the flushing flag until the tab died (STOP-SHIP).
function netTimeout(promise, ms, label) {
  let t = null;
  return Promise.race([
    promise,
    new Promise((_, rej) => { t = setTimeout(() => rej(new Error(`net_timeout:${label}`)), ms); }),
  ]).finally(() => clearTimeout(t));
}

export async function queueItem(item) {
  const record = { ...item, status: "pending", attempts: 0, error: "", created: Date.now(), next_retry_at: 0,
                   queued_offline: typeof navigator !== "undefined" ? !navigator.onLine : true };
  // Stable idempotency key: retries after reconnection can never duplicate.
  if (record.kind === "photo" && !record.client_id) record.client_id = crypto.randomUUID();
  if (record.kind === "original" && !record.client_id) throw new Error("original items need a client_id");
  if (record.preparing) record.preparing_since = Date.now(); // stuck-resize detection
  // Group A1: enqueue ONCE per client_id — a re-entrant caller (double-tap,
  // stale-closure finish) must never create a second entry for the same photo.
  if (record.client_id) {
    const dupe = await idbTx("queueItem.dedupe", "readonly", async (store) => {
      const idx = store.indexNames.contains("by_client_id") ? store.index("by_client_id") : null;
      const rows = idx ? await reqP(idx.getAll(record.client_id)) : await reqP(store.getAll());
      return (rows || []).find((i) => i.client_id === record.client_id && i.kind === record.kind) || null;
    });
    if (dupe) return dupe.qid;
  }
  const qid = await idbTx("queueItem.add", "readwrite", (store) => reqP(store.add(record)));
  notify();
  return qid;
}

export const isQuotaError = (e) =>
  e?.name === "QuotaExceededError" || /quota|storage/i.test(e?.message || "");

export async function getQueuedBlobByClientId(clientId) {
  // Round 18 (medium): the review screen shows the on-device copy of a photo
  // until its upload finishes — looks up the queued blob by client_id.
  if (!clientId) return null;
  try {
    const items = await getQueued();
    const hit = items.find((i) => i.kind === "photo" && i.client_id === clientId && blobOf(i));
    return hit ? blobOf(hit) : null;
  } catch { return null; }
}

export async function getQueued({ includeUploaded = true } = {}) {
  try {
    const items = (await idbTx("getQueued.read", "readonly", (store) => reqP(store.getAll()))) || [];
    const now = Date.now();
    const removed = new Set();
    // Prune only old "uploaded" confirmations — their photos already live on
    // the server with all proof metadata, so nothing is lost.
    for (const i of items) {
      if (i.status === "uploaded" && now - (i.uploaded_at || 0) > UPLOADED_KEEP_MS) removed.add(i.qid);
    }
    // Group A1 startup sweep: collapse duplicate entries for the same photo
    // (client_id) left by the re-entrant-finish bug — if one copy already
    // reached the server, drop the rest; otherwise keep the oldest entry.
    const byCid = new Map();
    for (const i of items) {
      if (!i.client_id || removed.has(i.qid)) continue;
      const key = `${i.kind}:${i.client_id}`;
      if (!byCid.has(key)) byCid.set(key, []);
      byCid.get(key).push(i);
    }
    for (const group of byCid.values()) {
      if (group.length < 2) continue;
      const keep = group.find((g) => g.status === "uploaded") || group.slice().sort((a, b) => a.qid - b.qid)[0];
      for (const g of group) if (g.qid !== keep.qid) removed.add(g.qid);
    }
    if (removed.size) {
      // ONE batched write transaction — never N connections for N deletes.
      const qids = [...removed];
      await idbTx("getQueued.sweep", "readwrite", (store) => { for (const q of qids) store.delete(q); })
        .catch((e) => console.warn("queue sweep failed", e));
      notify();
    }
    return items.filter((i) => !removed.has(i.qid)).filter((i) => includeUploaded || i.status !== "uploaded");
  } catch (e) {
    return [];
  }
}

// STOP-SHIP (A): upgrade legacy records IN PLACE on every app open — a photo
// stored under an old key must upload like any other, and anything unreadable
// or jobless is surfaced, never silently stranded (or sent to the Inbox).
// Wave 2: works on a SNAPSHOT; every write is individually bounded and
// failure-isolated, so one bad item can never lock the queue.
export async function migrateQueue() {
  let migrated = 0, unreadable = 0, needsJob = 0;
  let items;
  try {
    items = (await idbTx("migrate.read", "readonly", (store) => reqP(store.getAll()))) || [];
  } catch { return { migrated, unreadable, needsJob }; }
  for (const it of items) {
    if (!it || it.status === "uploaded" || it.kind !== "photo") continue;
    const blob = blobOf(it);
    const pid = projectOf(it);
    try {
      if (!blob) {
        if (it.status !== "unreadable") {
          unreadable += 1;
          await idbTx("migrate.unreadable", "readwrite", async (store) => {
            const cur = await reqP(store.get(it.qid));
            if (cur) store.put({ ...cur, status: "unreadable", error: "no photo data", next_retry_at: 0 });
          });
        }
        continue;
      }
      if (!pid) {
        if (it.status !== "needs_project") {
          needsJob += 1;
          await idbTx("migrate.needs_project", "readwrite", async (store) => {
            const cur = await reqP(store.get(it.qid));
            if (cur) store.put({ ...cur, status: "needs_project", error: "", next_retry_at: 0 });
          });
        }
        continue;
      }
      const legacy = !it.fileBlob || it.projectId === undefined || it.status === "unreadable" || it.status === "needs_project" || it.error === "network" || it.status === "uploading"
        || (it.status === "failed" && /HTTP (408|429|5\d\d)/.test(it.error || "")); // transient failures from older builds auto-heal
      if (legacy) {
        migrated += 1;
        const patch = { ...it, fileBlob: blob, projectId: pid, status: "pending", error: "", next_retry_at: 0 };
        delete patch.file; delete patch.blob; delete patch.data; // drop legacy keys on rewrite
        await idbTx("migrate.rewrite", "readwrite", (store) => { store.put(patch); }); // full rewrite without legacy keys
      }
    } catch (e) {
      // A single unwritable record must not hold the queue hostage.
      console.warn("migrate item failed", it.qid, e);
      reportQueueError(null, "migrate_item_failed", `${it.qid}: ${(e && e.message) || e}`, it);
    }
  }
  if (migrated || unreadable || needsJob) notify();
  return { migrated, unreadable, needsJob };
}

export async function updateQueued(qid, patch) {
  try {
    await idbTx("updateQueued", "readwrite", async (store) => {
      const cur = await reqP(store.get(qid));
      if (cur) store.put({ ...cur, ...patch });
    });
  } finally {
    notify();
  }
}

export async function removeQueued(qid) {
  await idbTx("removeQueued", "readwrite", (store) => { store.delete(qid); });
  notify();
}

export async function retryFailed() {
  const items = await getQueued();
  for (const it of items) {
    // Photos without a readable blob can't retry — everything else can.
    const needsBlob = it.kind === "photo" || it.kind === "original" || it.kind === "photo-annotate";
    if (["failed", "auth"].includes(it.status) && (!needsBlob || blobOf(it)))
      await updateQueued(it.qid, { status: "pending", error: "", next_retry_at: 0 });
    // Note: `fails` intentionally persists across manual retries (Round 18 A) —
    // it only disappears when the entry uploads and is removed, so the strikes
    // accumulate and the user gets the Save-to-phone offer exactly once.
  }
}

// A photo the user told us which job it belongs to.
export async function assignQueuedProject(qid, projectId) {
  await updateQueued(qid, { projectId, status: "pending", error: "", next_retry_at: 0 });
}

// ---- Settings › "Reset this device" (Phase 0): a clean start WITHOUT
// uninstalling. Uploads everything it can, downloads anything readable that
// couldn't upload, then wipes the local queue + SW caches and reloads.
async function downloadBlob(blob, filename) {
  try {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 10000);
    await new Promise((r) => setTimeout(r, 350)); // let each download start
  } catch { /* best effort */ }
}

export async function resetDevice(api, onProgress = () => {}) {
  const result = { uploaded: 0, saved: 0, cleared: 0 };
  // 1. Best-effort upload of everything readable (hard-bounded: the whole
  //    reset never hangs, whatever the network/store state).
  onProgress("uploading");
  try {
    await retryFailed();
    result.uploaded = await Promise.race([
      flushQueue(api),
      new Promise((r) => setTimeout(() => r(0), QUEUE_LIMITS.flushWatchdogMs)),
    ]);
  } catch { /* continue to save-to-phone */ }
  // 2. Anything left with a readable blob is saved to the phone as a file.
  onProgress("saving");
  const leftovers = await getQueued();
  for (const it of leftovers) {
    if (it.status === "uploaded") continue; // already on the server — nothing to rescue
    const b = blobOf(it);
    if (!b || (it.kind !== "photo" && it.kind !== "original")) continue;
    await downloadBlob(b, it.filename || `photo-${it.client_id || it.qid}.jpg`);
    result.saved += 1;
  }
  // 3. Clear the local queue store.
  onProgress("clearing");
  try {
    await idbTx("resetDevice.clear", "readwrite", (store) => { store.clear(); });
    result.cleared = leftovers.length;
  } catch (e) {
    reportQueueError(api, "reset_clear_failed", String((e && e.message) || e), null);
    hardResetDB();
  }
  // 4. Clear SW + browser caches (app shell re-downloads fresh on reload).
  try {
    if (typeof caches !== "undefined") {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
  } catch { /* best effort */ }
  notify();
  return result;
}

// ---- Done-time rescue (STOP-SHIP): a photo whose IndexedDB save outlives
// the camera's 2s budget waits HERE in memory and retries in the background —
// the camera closes instantly and the photo is never dropped.
const rescueList = new Map(); // client_id -> { payload, tries }
const RESCUE_MAX_TRIES = 40; // ~2 min of retries, then surface + report
export function getSavingCount() { return rescueList.size; }
function notifySaving() {
  try { window.dispatchEvent(new CustomEvent("bf:queue-saving", { detail: { count: rescueList.size } })); } catch { /* non-browser env */ }
}
export function rescueQueueItem(payload) {
  if (!payload || !payload.client_id || rescueList.has(payload.client_id)) return;
  rescueList.set(payload.client_id, { payload, tries: 0 });
  notifySaving();
  const attempt = async () => {
    const entry = rescueList.get(payload.client_id);
    if (!entry) return;
    entry.tries += 1;
    try {
      const qid = await queueItem({ ...entry.payload });
      rescueList.delete(payload.client_id);
      notifySaving();
      // If the capture-time chain died before clearing "preparing", release
      // the item so the drain can upload the raw blob.
      if (entry.payload.preparing) updateQueued(qid, { preparing: false }).catch(() => {});
      notify(); // queue-changed → auto-flush picks it up
      return;
    } catch (e) {
      if (entry.tries >= RESCUE_MAX_TRIES || isQuotaError(e)) {
        rescueList.delete(payload.client_id);
        notifySaving();
        reportQueueError(null, "rescue_failed", String((e && e.message) || e), entry.payload);
        try { window.dispatchEvent(new CustomEvent("bf:queue-save-failed", { detail: { client_id: payload.client_id } })); } catch { /* non-browser env */ }
        return;
      }
      setTimeout(attempt, 3000);
    }
  };
  setTimeout(attempt, 250);
}

// ---- Server-side visibility (Wave 2): the server can't see a stuck phone
// unless the phone says so. Throttled heartbeat of queue depth + age.
let lastStatusSent = 0;
let lastStatusPending = -1;
function reportQueueStatus(api) {
  const a = api || telemetryApi;
  (async () => {
    const now = Date.now();
    const items = await getQueued({ includeUploaded: false });
    const waiting = items.filter((i) => ["pending", "uploading", "failed", "auth"].includes(i.status));
    const oldest = waiting.reduce((m, i) => Math.max(m, Math.floor((now - (i.created || now)) / 60000)), 0);
    const due = now - lastStatusSent >= STATUS_REPORT_MS
      || (waiting.length === 0 && lastStatusPending > 0)
      || (waiting.length > 0 && lastStatusPending === 0);
    if (!due) return;
    lastStatusSent = now;
    lastStatusPending = waiting.length;
    await netTimeout(a.post("/photos/queue-status", {
      pending: waiting.length, oldest_min: oldest,
      device: (typeof navigator !== "undefined" ? navigator.userAgent : "").slice(0, 120),
    }), QUEUE_LIMITS.netMs.status, "queue-status");
  })().catch(() => {});
}

// (photoFormData removed — photos upload via the B1 ticket path: PUT bytes
// direct, then commit metadata. See uploadOne.)

// One upload attempt. Returns "ok" | "drop" | "fail" | "retry".
// "retry" = transient: back off and let the other photos through —
// head-of-line blocking is banned (Fast Photos E).
async function uploadOne(api, item) {
  // STOP-SHIP (A): normalize legacy records before anything touches them.
  const blob = blobOf(item);
  const pid = projectOf(item);
  if (item.kind === "photo" && !blob) {
    await updateQueued(item.qid, { status: "unreadable", error: "no photo data" });
    reportQueueError(api, "unreadable", "no photo data", item);
    return "drop";
  }
  if (item.kind === "photo" && !pid) {
    await updateQueued(item.qid, { status: "needs_project", error: "" });
    return "drop";
  }
  await updateQueued(item.qid, { status: "uploading", attempts: (item.attempts || 0) + 1, lease_until: Date.now() + UPLOAD_LEASE_MS });
  try {
    if (item.kind === "photo") {
      // B1 ticket path: signed ticket → direct PUT (bytes) → idempotent
      // commit (metadata only). The old multipart POST /api/photos did its
      // processing inline — that was the 2.7s+ per-upload server time on
      // bursts. Commit is safe to repeat on (tenant, client_id).
      let tk = await netTimeout(api.post("/photos/upload-url", { content_type: "image/jpeg" }),
        QUEUE_LIMITS.netMs.refresh, "ticket");
      // Two put modes: "raw" = pre-signed R2/S3 URL (bytes go STRAIGHT to
      // storage — absolute URL, raw body); "multipart" = our /direct ticket.
      let putRes = null;
      if (tk.data.mode === "raw") {
        // Direct-to-storage: try TWICE, then fall back THROUGH the app
        // server. A storage/CORS misconfig must never leave photos stuck
        // (2026-10-20: bucket without a CORS policy silently failed every
        // browser PUT). The fallback is reported to Admin > Upload health.
        let lastErr = null;
        for (let attempt = 0; attempt < 2; attempt++) {
          try {
            putRes = await netTimeout(
              fetch(tk.data.put_url, { method: "PUT", body: blob, headers: { "Content-Type": "image/jpeg" } }),
              QUEUE_LIMITS.netMs.photo + 5000, "photo-put");
            if (putRes.ok || putRes.status === 409) break;
            lastErr = new Error(`HTTP ${putRes.status}`);
            putRes = null;
          } catch (e) { lastErr = e; putRes = null; }
        }
        if (!putRes) {
          reportQueueError(api, "r2_fallback", `direct PUT failed twice: ${String((lastErr && lastErr.message) || lastErr)}`, item);
          tk = await netTimeout(api.post("/photos/upload-url", { content_type: "image/jpeg", prefer: "local" }),
            QUEUE_LIMITS.netMs.refresh, "ticket-fallback");
        }
      }
      if (!putRes) {
        const putBody = new FormData();
        putBody.append("file", blob, item.filename || "photo.jpg");
        putBody.append("client_id", item.client_id || "");
        putRes = await netTimeout(
          fetch(`${API.replace(/\/api$/, "")}${tk.data.put_url}`, { method: "PUT", body: putBody }),
          QUEUE_LIMITS.netMs.photo + 5000, "photo-put");
      }
      if (putRes.status !== 409 && !putRes.ok) {
        // 409 = bytes already landed (crash between PUT and commit) → commit.
        const e = new Error(`HTTP ${putRes.status}`);
        e.response = { status: putRes.status };
        throw e;
      }
      await netTimeout(api.post("/photos/commit", {
        upload_id: tk.data.upload_id, client_id: item.client_id, project_id: projectOf(item),
        note: item.note || "", tag: item.tag || "", category: item.category || "",
        lat: item.lat ?? null, lng: item.lng ?? null, accuracy_m: item.accuracy_m ?? null,
        device_capture_ts: item.device_capture_ts || "", device_tz: item.device_tz || "",
        location_source: item.location_source || "", source: item.source || "camera",
        walkthrough_id: item.walkthrough_id || "", wt_t: item.wt_t ?? null,
        offline_created: !!item.queued_offline, filename: item.filename || "",
      }), QUEUE_LIMITS.netMs.photo, "photo-commit");
    } else if (item.kind === "original") {
      const fd = new FormData();
      fd.append("client_id", item.client_id || "");
      fd.append("file", blob, item.filename || "original.jpg");
      await netTimeout(
        api.post("/photos/original", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: QUEUE_LIMITS.netMs.original }),
        QUEUE_LIMITS.netMs.original + 5000, "original");
    } else if (item.kind === "photo-annotate") {
      const fd = new FormData();
      fd.append("file", blob, item.client_id || "");
      await netTimeout(
        api.post(`/photos/${item.photo_id}/annotate`, fd, { headers: { "Content-Type": "multipart/form-data" } }),
        QUEUE_LIMITS.netMs.photo + 5000, "photo-annotate");
    } else if (item.kind === "log") {
      await netTimeout(api.post("/logs", { projectId: pid, project_id: pid, text: item.text, lang: item.lang, lat: item.lat ?? null, lng: item.lng ?? null }),
        QUEUE_LIMITS.netMs.log, "log");
    } else if (item.kind === "time_event") {
      await netTimeout(api.post(item.endpoint, item.payload), QUEUE_LIMITS.netMs.time_event, "time_event");
    }
    // Confirmed on the server — photo entries stay as a 1h "uploaded"
    // confirmation so grids swap the local blob for the server copy in place;
    // everything else is deleted (the server dedupes retries by client_id).
    if (item.kind === "photo") {
      try { window.dispatchEvent(new CustomEvent("bf:photo-uploaded", { detail: { client_id: item.client_id, projectId: pid } })); } catch (e) { /* non-browser env */ }
      await updateQueued(item.qid, { status: "uploaded", uploaded_at: Date.now(), error: "" });
      return "ok";
    }
    await removeQueued(item.qid);
    return "ok";
  } catch (e) {
    if (e.response && (e.response.status === 410 || e.response.status === 404)) {
      // P0-7: job deleted/trashed server-side — NEVER lose the photo and never
      // retry forever. Park it as "orphaned": out of the waiting count, listed
      // under Settings › Photos on this phone with Upload now / Save to phone.
      await updateQueued(item.qid, { status: "orphaned", error: "job deleted" });
      reportQueueError(api, "orphaned", "job deleted", item);
      return "drop";
    }
    if (e.response) {
      const s = e.response.status;
      if (s === 401 || s === 403) {
        // B6: try a silent token refresh once (httpOnly refresh cookie) — an
        // expired login must not strand photos behind a manual tap.
        if (!item.auth_retried) {
          try {
            const r = await netTimeout(api.post("/auth/refresh"), QUEUE_LIMITS.netMs.refresh, "refresh");
            if (r?.data?.access_token) setToken(r.data.access_token);
            await updateQueued(item.qid, { status: "pending", error: "", auth_retried: true, next_retry_at: Date.now() + 1000 });
            return "retry";
          } catch { /* refresh impossible — fall through to the auth state */ }
        }
        // Refresh failed (or already tried): nothing retries until sign-in.
        await updateQueued(item.qid, { status: "auth", error: `HTTP ${s}` });
        reportQueueError(api, "auth", `HTTP ${s}`, item);
        try { window.dispatchEvent(new CustomEvent("bf:queue-auth", { detail: {} })); } catch { /* non-browser env */ }
        return "drop";
      }
      if (s === 413 && blob && !item.retried_413) {
        // Too large: recompress harder (1600px) and retry once.
        try {
          const prep = await prepareImageForUpload(blob, { maxSide: 1600, quality: 0.7 });
          await updateQueued(item.qid, { fileBlob: prep.blob, status: "pending", error: "", retried_413: true, next_retry_at: Date.now() + 2000 });
          return "retry";
        } catch { /* fall through to failed */ }
      }
      if (s === 408 || s === 429 || s >= 500) {
        // Transient server trouble — retry with backoff forever (B6).
        const attempts = (item.attempts || 0) + 1;
        await updateQueued(item.qid, { status: "pending", attempts, error: `HTTP ${s}`, next_retry_at: Date.now() + backoffFor(attempts) });
        return "retry";
      }
      // 400/422 and friends: permanent — keep the REAL error visible per photo.
      const fails = (item.fails || 0) + 1;
      await updateQueued(item.qid, { status: "failed", error: `HTTP ${s}`, fails });
      if (fails === DEAD_FAILS) {
        try { window.dispatchEvent(new CustomEvent("bf:queue-dead", { detail: { qid: item.qid } })); } catch (e2) { /* non-browser env */ }
        reportQueueError(api, "permanent_failure", item.error, item);
      }
      return "fail";
    }
    // Network down / timeout: back off (5s → 15s → 30s → 60s → 2min) and
    // keep draining the rest — one stuck photo never blocks the others.
    const attempts = (item.attempts || 0) + 1;
    await updateQueued(item.qid, { status: "pending", attempts, error: "network", next_retry_at: Date.now() + backoffFor(attempts) });
    return "retry";
  }
}

let dueTimer = null;
let autoCtx = null; // {api, onSynced} remembered for backoff-triggered retries

function scheduleDueCheck() {
  if (!autoCtx || dueTimer) return;
  getQueued({ includeUploaded: false }).then((items) => {
    const nexts = items
      .filter((i) => (i.status === "pending" || i.status === "uploading") && i.next_retry_at)
      .map((i) => i.next_retry_at);
    if (!nexts.length) return;
    const wait = Math.max(500, Math.min(...nexts) - Date.now());
    dueTimer = setTimeout(() => {
      dueTimer = null;
      flushQueue(autoCtx.api, autoCtx.onSynced); // a real attempt decides — never trust navigator.onLine
    }, wait);
  }).catch(() => {});
}

async function drain(api, onSynced) {
  let synced = 0;
  let syncedOffline = 0; // only items queued while offline earn the "synced" toast
  // Drain loop: items queued WHILE a flush is running (rapid capture,
  // background writes) upload in the same pass — never stranded pending.
  for (;;) {
    const items = await getQueued();
    const now = Date.now();
    const due = items.filter((i) =>
      (i.status === "pending" || (i.status === "uploading" && !(i.lease_until > now))) && // a live lease means another worker owns it
      !(i.preparing && now - (i.preparing_since || i.created || 0) < PREPARING_STALE_MS) && // still resizing on-device — but a stuck resize (60s+) uploads the raw copy rather than stranding the photo
      !inFlight.has(i.qid) && // never send the same item twice in parallel
      !(i.next_retry_at && i.next_retry_at > now) &&
      (i.kind !== "original" || isOnWifiNow()));
    if (!due.length) break;
    const batch = due.slice(0, CONCURRENCY);
    batch.forEach((i) => inFlight.add(i.qid));
    let progressed = false;
    await Promise.all(batch.map(async (item) => {
      try {
        const res = await uploadOne(api, item);
        if (res === "ok") { synced += 1; if (item.queued_offline !== false) syncedOffline += 1; }
        if (res !== "retry") progressed = true;
      } finally {
        inFlight.delete(item.qid);
      }
    }));
    if (!progressed) break; // everything hit the network — wait for the next trigger
  }
  if (syncedOffline > 0 && onSynced) onSynced(syncedOffline);
  scheduleDueCheck(); // fire the next backoff retry exactly when it comes due
  return synced;
}

export async function flushQueue(api, onSynced) {
  if (flushing) return 0; // single-flight: concurrent flushes can't double-upload
  flushing = true;
  let synced = 0;
  try {
    const work = (async () => {
      // Cross-tab: only one tab drains (Web Lock). Absent in jsdom — guarded.
      if (typeof navigator !== "undefined" && navigator.locks?.request) {
        // STOP-SHIP (prod bug 2026.10.10.0306): {ifAvailable} and {signal} are
        // MUTUALLY EXCLUSIVE in the Web Locks API — combining them throws in
        // every real browser and the queue never drained. The 60s cap is
        // enforced INSIDE the callback; ANY lock error falls back to a
        // lockless drain — the upload is never skipped.
        try {
          await navigator.locks.request("bf-flush", { ifAvailable: true }, async (lock) => {
            if (!lock) return;
            await Promise.race([
              drain(api, onSynced).then((n) => { synced = n; }),
              // Max hold: release the lock after 60s even if a drain stalls;
              // per-item leases keep the still-running drain safe.
              new Promise((r) => setTimeout(r, QUEUE_LIMITS.lockHoldMs)),
            ]);
          });
        } catch (e) {
          reportQueueError(api, "lock_error", String((e && e.message) || e), null);
          synced = await drain(api, onSynced); // never skip the upload
        }
      } else {
        synced = await drain(api, onSynced);
      }
    })();
    try {
      // Watchdog: whatever happens inside, the flush ALWAYS settles — a stuck
      // flushing flag used to brick the queue until the tab died.
      await Promise.race([
        work,
        new Promise((_, rej) => setTimeout(() => rej(new Error("flush_watchdog")), QUEUE_LIMITS.flushWatchdogMs)),
      ]);
    } catch (e) {
      reportQueueError(api, "flush_watchdog", String((e && e.message) || e), null);
    }
    reportQueueStatus(api);
    return synced;
  } finally {
    flushing = false;
  }
}

// Fast Photos (E) + B6: retries run on app open, foreground return, network
// change and a 30s watchdog — never gated on navigator.onLine (weak signal
// keeps the phone "online" while every request times out; a real attempt
// decides whether we're online).
export function startAutoFlush(api, onSynced) {
  if (autoCtx || typeof window === "undefined") return;
  autoCtx = { api, onSynced };
  const kick = () => { flushQueue(api, onSynced).catch(() => {}); };
  window.addEventListener("online", kick);
  document.addEventListener("visibilitychange", () => { if (document.visibilityState === "visible") kick(); });
  try { navigator.connection?.addEventListener?.("change", kick); } catch { /* unsupported */ }
  let qTimer = null;
  window.addEventListener("bf:queue-changed", () => {
    if (qTimer) return;
    qTimer = setTimeout(() => { qTimer = null; kick(); }, 400); // debounce burst capture
  });
  setInterval(kick, 30000);
  kick();
}
